import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import useReveal from '../useReveal';
import './AIEFaq.css';
import { COST_FAQ_ANSWER } from '../../../content/pricingFigures';

/**
 * AIEFaq — the FAQ beat on the AI Employees landing.
 *
 * Two jobs:
 *  1. UX: answer the real questions a buyer asks before hiring an AI employee.
 *  2. GEO/SEO: the parent page renders FAQPage JSON-LD built from THIS exact
 *     list (see AIE_FAQ_ITEMS export), so the structured data always matches
 *     the visible content — AI answer engines quote FAQ Q&A directly.
 *
 * Merged list (Aug 5 revamp, Nitish-approved): definitional items first (the
 * AEO assets — "what is", "vs chatbot", "vs autonomous agent/digital worker"),
 * buyer-conversion items second (cost, credits, control, no sales call, data).
 * The cost answer is the shared COST_FAQ_ANSWER (src/content/pricingFigures.js;
 * canon Sep 6 2026: cost figures live in four homes and this string is one).
 * Content is grounded only in real product behavior — no invented claims.
 */
export const AIE_FAQ_ITEMS = [
  {
    question: 'What is an AI employee?',
    answer:
      "An AI employee is a general-purpose AI worker you hire for any role: marketing, growth, engineering, sales outreach, research, support, operations. You set the goals and permissions; they work real shifts with their own inbox and pull in your other AI employees when the job needs them. They can reason deeply across research, data, code, documents, and media, not just one narrow task.",
  },
  {
    question: 'How is an AI employee different from a chatbot or assistant?',
    answer:
      'A chatbot waits for you to prompt it, one message at a time. An AI employee owns a role: their own email inbox, channels, a task board, persistent memory across shifts, and the autonomy to act on the goals and permissions you set, including handing work to your other AI employees or back to you when they need a decision.',
  },
  {
    question: 'What about autonomous AI agents? How is this different?',
    answer:
      'An autonomous agent runs the task you hand it, then forgets you. An AI employee, sometimes called a digital worker, holds a standing role with long-term memory: shift 50 remembers the decisions from shift 1. They keep their own inbox, channels, and task board, coordinate with coworkers, and close every shift with a written report back to you. You are hiring, not running a task.',
  },
  {
    question: 'What roles can I hire an AI employee for?',
    answer:
      'Any role. Every AI employee has the same full capabilities, so you define the role rather than picking from a fixed menu: marketing, growth, engineering, sales outreach, customer support, research, design, data analysis, reporting, operations, and more.',
    // Visible render links the role names to their role pages (internal-linking
    // pass CEL-1309, Aug 2026): contextual anchors from this indexed page pass
    // equity to the role cluster. JSON-LD stays the plain string above.
    answerJsx: (
      <>
        Any role. Every AI employee has the same full capabilities, so you
        define the role rather than picking from a fixed menu: marketing,
        growth, <a href="/ai-employees/ai-software-engineer">engineering</a>,{' '}
        <a href="/ai-employees/ai-sdr">sales outreach</a>,{' '}
        <a href="/ai-employees/ai-customer-support-agent">customer support</a>,
        research, <a href="/ai-employees/ai-designer">design</a>, data
        analysis, reporting, operations, and more.
      </>
    ),
  },
  {
    question: 'Can multiple AI employees work together?',
    answer:
      "Yes, that's the point. You build the org chart; they manage each other inside it. They hand tasks to each other, onboard new coworkers, and pull you in when a decision needs your judgment. Our own company runs this way.",
  },
  {
    question: 'Can an AI employee use Claude Code, Codex or Cursor?',
    answer:
      'Yes. An AI employee, or a regular CellCog chat, can hand a piece of work to a subagent: Claude Code, Codex or Cursor running on your own computer under your own plan, another CellCog chat for research and media, or any tool that can call a link when it is done. The employee or chat steps away while the subagent works, then reads the result and marks it reviewed before telling you the job is done.',
    // Visible render links the launch post; JSON-LD stays the plain string above.
    answerJsx: (
      <>
        Yes. An AI employee, or a regular CellCog chat, can hand a piece of work to a subagent: Claude Code, Codex or Cursor running on your own computer under your own plan, another CellCog chat for research and media, or any tool that can call a link when it is done. The employee or chat steps away while the subagent works, then reads the result and marks it reviewed before telling you the job is done.{' '}
        <a href="/blog/ai-employee-subagents/">How subagents work</a>.
      </>
    ),
  },
  {
    question: 'Can I hire AI employees?',
    answer:
      'Yes, today. The hire flow is self-serve and takes under a minute: you pick the role, your new AI employee introduces themselves in the chat, and hiring is free. You pay only for the work they do.',
  },
  {
    question: 'How much is an AI employee?',
    answer:
      COST_FAQ_ANSWER,
  },
  {
    question: 'What happens when credits run out?',
    answer:
      'Work pauses safely and nothing is lost. Top up your credits or turn on auto-reload, and your employee picks up exactly where they left off, with one message to resume the shift.',
  },
  {
    question: 'Do I stay in control of what they do?',
    answer:
      'Yes. You set the permissions, anything dangerous waits for your approval, and every task and email is visible on one board.',
  },
  {
    question: 'Do I need a sales call or setup help?',
    answer:
      'No. The hire flow is self-serve and takes under a minute. You can sign up and try it free with 200 credits, no credit card needed. And if you want help or more information at any point, write to us through the contact page.',
    // Visible render carries the clickable link; JSON-LD stays the plain string above.
    answerJsx: (
      <>
        No. The hire flow is self-serve and takes under a minute. You can sign
        up and try it free with 200 credits, no credit card needed. And if you
        want help or more information at any point, write to us through the{' '}
        <a href="/contact">contact page</a>.
      </>
    ),
  },
  {
    question: 'How is my data handled?',
    answer:
      'Your employee only works inside the permissions you grant, and those permissions are set up front as part of onboarding: you decide which tools, accounts, and data they can touch before they start, and anything dangerous waits for your approval. You can review or revoke any permission at any time.',
    // Visible render links "tools" to the public Tools page (Nov 3 2026); JSON-LD stays the plain string above.
    answerJsx: (
      <>
        Your employee only works inside the permissions you grant, and those
        permissions are set up front as part of onboarding: you decide which{' '}
        <Link to="/tools" className="aie-inline-link">tools</Link>, accounts, and data they can touch before
        they start, and anything dangerous waits for your approval. You can
        review or revoke any permission at any time.
      </>
    ),
  },
];

/* Shared accordion (Nov 3 2026, the /tools page FAQ): every prop defaults to the AI Employees FAQ, so that page renders
   exactly as before. `trackOpen` = a data-track name put on a CLOSED question only (the delegated app_click listener reads
   it at click time, before the toggle), with data-track-detail = the question index: one event per open, none per close. */
const AIEFaq = ({
  items = AIE_FAQ_ITEMS,
  eyebrow = 'Questions',
  title = 'Questions buyers actually ask',
  sub = 'The honest answers to what an AI employee actually is, and how it works.',
  titleId = 'aie-faq-title',
  trackOpen,
}) => {
  const [ref, visible] = useReveal();
  const [open, setOpen] = useState(null);

  const toggle = (i) => setOpen((cur) => (cur === i ? null : i));

  return (
    <section className="aie-faq aie-section" ref={ref} aria-labelledby={titleId}>
      <div className={`aie-section-head aie-reveal ${visible ? 'is-visible' : ''}`}>
        <span className="aie-eyebrow">{eyebrow}</span>
        <h2 id={titleId} className="aie-section-title aie-display">
          {title}
        </h2>
        {sub && <p className="aie-section-sub">{sub}</p>}
      </div>

      <div className={`aie-faq__list aie-reveal ${visible ? 'is-visible' : ''}`}>
        {items.map((item, i) => (
          <div
            key={item.question}
            className={`aie-faq__item ${open === i ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="aie-faq__q"
              aria-expanded={open === i}
              data-track={trackOpen && open !== i ? trackOpen : undefined}
              data-track-detail={trackOpen && open !== i ? String(i) : undefined}
              onClick={() => toggle(i)}
            >
              <span>{item.question}</span>
              <PlusOutlined className="aie-faq__icon" />
            </button>
            <div className="aie-faq__a-wrap" role="region">
              <p className="aie-faq__a">{item.answerJsx || item.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AIEFaq;
